import React, { useEffect, useMemo, useState, Component } from 'react';
/**
 * FeedbackSplash — Full-screen celebration/encouragement overlay.
 *
 * Shows after answering a question: confetti + Djay for correct,
 * warm floating particles + encouragement for incorrect.
 * Auto-advances after a configurable duration, tap to skip.
 *
 * Optimized s64: particle data + display config extracted to feedbackSplashData.ts
 * (667→~420 lines). Co-located in practice/ domain (CL-1 s64).
 */
import { motion, useReducedMotion } from 'framer-motion';
import { Star, Sparkles } from 'lucide-react';
import { DjayAvatar, useDjaySelection, getDjaySplashMessage, type DjayTier, type DjaySkin } from '../../../mascot';
import { soundManager } from '../../../utils/soundManager';
import { CONFETTI_PARTICLES, EXTRA_CONFETTI, WARM_PARTICLES, buildSplashDisplayConfig, resolveSplashPriority, type ConfettiParticle, type WarmParticle } from './feedbackSplashData';
// ── Types ────────────────────────────────────────────────────
interface FeedbackSplashProps {
  isCorrect: boolean;
  xpGained: number;
  streak?: number;
  onComplete: () => void;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
  streakMilestone?: {
    count: number;
    message: string;
  } | null;
  dailyGoalReached?: boolean;
  tierEvolution?: {
    from: DjayTier;
    to: DjayTier;
  } | null;
  skinUnlock?: {
    skin: DjaySkin;
    description: string;
  } | null;
}
// ── Shared Sub-components ────────────────────────────────────
function ConfettiLayer({
  particles,
  reduced
}: {
  particles: ConfettiParticle[];
  reduced: boolean | null;
}) {
  if (reduced) return null;
  return <>
      {particles.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.color} ${p.size} pointer-events-none opacity-80`} style={{
      left: `${p.startX}%`,
      top: `${p.startY}%`
    }} initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      x: `${(p.endX - p.startX) * 2.5}vw`,
      y: `${p.endY - p.startY}vh`,
      scale: [0, 1.2, 1, 0.6],
      opacity: [0, 0.9, 0.9, 0],
      rotate: p.rotation
    }} transition={{
      duration: p.duration,
      delay: p.delay,
      ease: 'easeOut'
    }} data-id="element-6888" />)}
    </>;
}
function WarmParticleLayer({
  particles,
  reduced
}: {
  particles: WarmParticle[];
  reduced: boolean | null;
}) {
  if (reduced) return null;
  return <>
      {particles.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.color} ${p.size} pointer-events-none opacity-50`} style={{
      left: `${p.startX}%`,
      bottom: '0%'
    }} animate={{
      y: [`${p.startY}vh`, `${p.endY}vh`],
      x: [0, p.drift],
      opacity: [0, 0.5, 0.5, 0]
    }} transition={{
      duration: p.duration,
      delay: p.delay,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-6889" />)}
    </>;
}
// ── Hooks ────────────────────────────────────────────────────
function useXpCounter(target: number) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (target === 0) return;
    const steps = 15;
    const increment = target / steps;
    const interval = 500 / steps;
    let current = 0;
    const id = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDisplay(target);
        soundManager.playTick();
        clearInterval(id);
      } else {
        setDisplay(Math.floor(current));
        soundManager.playTick();
      }
    }, interval);
    return () => clearInterval(id);
  }, [target]);
  return display;
}
// ── Component ────────────────────────────────────────────────
export function FeedbackSplash({
  isCorrect,
  xpGained,
  streak = 0,
  onComplete,
  levelUp,
  streakMilestone,
  dailyGoalReached,
  tierEvolution,
  skinUnlock
}: FeedbackSplashProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const shouldReduceMotion = useReducedMotion();
  const xpDisplay = useXpCounter(xpGained);
  const splashMsg = useMemo(() => getDjaySplashMessage(isCorrect, streak), [isCorrect, streak]);
  // Play sound feedback on mount
  useEffect(() => {
    if (levelUp) {
      soundManager.playLevelUp();
    } else if (dailyGoalReached) {
      soundManager.playGoalReached();
    } else if (streakMilestone) {
      soundManager.playStreak();
    } else if (isCorrect) {
      soundManager.playCorrect();
    } else {
      soundManager.playWrong();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const config = useMemo(() => buildSplashDisplayConfig({
    isCorrect,
    streak,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
    splashMsg
  }), [isCorrect, streak, levelUp, tierEvolution, skinUnlock, dailyGoalReached, streakMilestone, splashMsg]);
  const priority = useMemo(() => resolveSplashPriority({
    isCorrect,
    streak,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
    splashMsg
  }), [isCorrect, streak, levelUp, tierEvolution, skinUnlock, dailyGoalReached, streakMilestone, splashMsg]);
  const {
    mainMessage,
    subMessage,
    emotion,
    duration,
    gradient,
    showExtraConfetti
  } = config;
  const noMotion = !!shouldReduceMotion;
  // Auto-advance timer
  useEffect(() => {
    const timer = setTimeout(onComplete, duration);
    return () => clearTimeout(timer);
  }, [duration, onComplete]);
  // ── Correct Splash ──
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0,
      scale: noMotion ? 1 : 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: noMotion ? 1 : 1.05
    }} transition={{
      duration: 0.4,
      ease: [0.175, 0.885, 0.32, 1.275] // Custom spring-like ease
    }} onClick={onComplete} className="min-h-[calc(100vh-80px)] w-full flex flex-col items-center justify-center overflow-hidden cursor-pointer relative rounded-3xl" style={{
      background: gradient
    }} data-id="element-6890">
        {/* Radial glow */}
        {!noMotion && <motion.div className="absolute w-[400px] h-[400px] rounded-full pointer-events-none bg-white/60 blur-3xl" initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: [0, 1.5, 1.2],
        opacity: [0, 1, 0.8]
      }} transition={{
        duration: 1.2,
        ease: 'easeOut',
        times: [0, 0.4, 1]
      }} data-id="element-6891" />}

        <ConfettiLayer particles={CONFETTI_PARTICLES} reduced={shouldReduceMotion} data-id="element-6892" />
        {showExtraConfetti && <ConfettiLayer particles={EXTRA_CONFETTI} reduced={shouldReduceMotion} data-id="element-6893" />}

        {/* Djay Avatar */}
        <motion.div initial={{
        scale: noMotion ? 1 : 0,
        rotate: noMotion ? 0 : -25,
        y: noMotion ? 0 : 60
      }} animate={{
        scale: 1,
        rotate: 0,
        y: noMotion ? 0 : [0, -12, 0, -6, 0]
      }} transition={{
        scale: {
          type: 'spring',
          stiffness: 400,
          damping: 15,
          delay: noMotion ? 0 : 0.1
        },
        rotate: {
          type: 'spring',
          stiffness: 400,
          damping: 15,
          delay: noMotion ? 0 : 0.1
        },
        y: {
          delay: noMotion ? 0 : 0.4,
          duration: noMotion ? 0 : 1.5,
          repeat: noMotion ? 0 : Infinity,
          repeatType: 'reverse',
          ease: 'easeInOut'
        }
      }} className="relative z-10 mb-6" data-id="element-6894">
          <DjayAvatar size="hero" emotion={emotion} variant={selectedVariant} animate={!noMotion} data-id="element-6895" />
        </motion.div>

        {/* Main text */}
        <motion.h1 initial={{
        scale: noMotion ? 1 : 0.5,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 30
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 18,
        delay: noMotion ? 0 : 0.25
      }} className="text-4xl sm:text-6xl font-black text-amber-900 mb-4 tracking-tight text-center px-6" style={{
        textShadow: '0 4px 12px rgba(217, 119, 6, 0.2)'
      }} data-id="element-6896">
          {mainMessage}
        </motion.h1>

        {subMessage && <motion.p initial={{
        scale: noMotion ? 1 : 0.8,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: noMotion ? 0 : 0.35
      }} className="text-2xl sm:text-3xl font-bold text-amber-700 mb-8 text-center px-6" data-id="element-6897">
            {subMessage}
          </motion.p>}

        {/* Badges */}
        <motion.div initial={{
        scale: noMotion ? 1 : 0.5,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        delay: noMotion ? 0 : 0.45,
        type: 'spring',
        stiffness: 350,
        damping: 20
      }} className="flex flex-col items-center gap-4" data-id="element-6898">
          <motion.div whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/90 backdrop-blur-md border-2 border-amber-300 shadow-xl shadow-amber-200/40" data-id="element-6899">
            <motion.div animate={{
            rotate: noMotion ? 0 : [0, 15, -15, 0],
            scale: noMotion ? 1 : [1, 1.2, 1]
          }} transition={{
            duration: noMotion ? 0 : 0.6,
            delay: noMotion ? 0 : 0.6,
            repeat: noMotion ? 0 : Infinity,
            repeatDelay: 2
          }} data-id="element-6900">
              <Star className="w-6 h-6 fill-amber-500 text-amber-500" data-id="element-6901" />
            </motion.div>
            <span className="text-2xl font-black text-amber-800" data-id="element-6902">
              +{xpDisplay} XP
            </span>
          </motion.div>

          <div className="flex gap-3 mt-2" data-id="element-6903">
            {streak >= 2 && <motion.div initial={{
            scale: noMotion ? 1 : 0,
            opacity: noMotion ? 1 : 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            delay: noMotion ? 0 : 0.8
          }} className="px-4 py-1.5 rounded-full bg-orange-100 border-2 border-orange-200 text-orange-700 text-sm font-black shadow-sm flex items-center gap-1.5" data-id="element-6904">
                🔥 {streak}x Combo!
              </motion.div>}
            {priority === 'levelUp' && streakMilestone && <div className="px-3 py-1 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-xs font-bold shadow-sm" data-id="element-6905">
                🔥 {streakMilestone.count} op rij
              </div>}
            {priority === 'levelUp' && dailyGoalReached && <div className="px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-bold shadow-sm" data-id="element-6906">
                ✅ Dagelijks doel
              </div>}
          </div>
        </motion.div>
      </motion.div>;
  }
  // ── Incorrect Splash ──
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeInOut'
  }} onClick={onComplete} className="min-h-[calc(100vh-80px)] w-full flex flex-col items-center justify-center overflow-hidden cursor-pointer relative rounded-3xl" style={{
    background: gradient
  }} data-id="element-6907">
      {!noMotion && <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-white blur-3xl pointer-events-none" animate={{
      opacity: [0.1, 0.2, 0.1]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-6908" />}

      <WarmParticleLayer particles={WARM_PARTICLES} reduced={shouldReduceMotion} data-id="element-6909" />

      <motion.div initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 30,
      scale: noMotion ? 1 : 0.85
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1,
      rotate: noMotion ? 0 : [0, -2, 2, -1, 0]
    }} transition={{
      opacity: {
        duration: noMotion ? 0 : 0.5,
        ease: 'easeOut',
        delay: noMotion ? 0 : 0.1
      },
      y: {
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: noMotion ? 0 : 0.1
      },
      scale: {
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: noMotion ? 0 : 0.1
      },
      rotate: {
        delay: noMotion ? 0 : 0.6,
        duration: noMotion ? 0 : 0.8,
        ease: 'easeInOut'
      }
    }} className="relative z-10 mb-6" data-id="element-6910">
        <DjayAvatar size="hero" emotion={emotion} variant={selectedVariant} animate={!noMotion} data-id="element-6911" />
      </motion.div>

      <motion.h1 initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.25,
      duration: noMotion ? 0 : 0.4,
      ease: 'easeOut'
    }} className="text-4xl sm:text-5xl font-black text-slate-800 mb-3 tracking-tight text-center px-6" data-id="element-6912">
        {mainMessage}
      </motion.h1>

      <motion.p initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 15
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.35,
      duration: noMotion ? 0 : 0.4,
      ease: 'easeOut'
    }} className="text-xl sm:text-2xl text-slate-600 font-semibold mb-8 text-center px-6" data-id="element-6913">
        {subMessage}
      </motion.p>

      <motion.div initial={{
      scale: noMotion ? 1 : 0.8,
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 15
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.45,
      type: 'spring',
      stiffness: 400,
      damping: 25
    }} className="flex flex-col items-center gap-2" data-id="element-6914">
        <div className="flex items-center gap-2.5 px-6 py-3 rounded-full bg-orange-100 border-2 border-orange-200 shadow-[0_8px_30px_rgb(249,115,22,0.2)]" data-id="element-6915">
          <Sparkles className="w-6 h-6 fill-orange-500 text-orange-500" data-id="element-6916" />
          <span className="text-xl font-black text-orange-700" data-id="element-6917">
            +{xpDisplay} XP
          </span>
          <span className="text-sm font-bold text-orange-600 uppercase tracking-wider" data-id="element-6918">
            voor de inzet
          </span>
        </div>
      </motion.div>
    </motion.div>;
}